import React, { useState } from 'react';
import { useVault } from './VaultContext';
import { useAuth } from '../auth/AuthContext';
import { apiPost } from '../api/client';
import AlertMessage from './AlertMessage';
import './BugReportModal.css';

interface Props {
  onClose: () => void;
}

type Severity = 'looks-off' | 'cant-complete' | 'broken';

export default function BugReportModal({ onClose }: Props) {
  const { user } = useAuth();
  const { activeVaultId, activeVaultName, activeVaultUserRole, activeVaultAccountTier } = useVault();

  const [description, setDescription] = useState('');
  const [severity, setSeverity] = useState<Severity | null>(null);
  const [reporterEmail, setReporterEmail] = useState('');
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const viewport = `${window.innerWidth} × ${window.innerHeight}`;
  const pageUrl = window.location.pathname;
  const appVersion = process.env.REACT_APP_VERSION ?? 'unknown';

  const canSubmit = description.trim().length > 0 && severity !== null && !submitting;

  async function handleSubmit() {
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    try {
      await apiPost('/api/support/bug-report', {
        reporterEmail: !user && reporterEmail.trim() ? reporterEmail.trim() : undefined,
        description: description.trim(),
        severity,
        pageUrl,
        userAgent: navigator.userAgent,
        viewport,
        vaultId: activeVaultId ?? null,
        vaultName: activeVaultName ?? null,
        userRole: activeVaultUserRole !== undefined ? String(activeVaultUserRole) : null,
        accountTier: activeVaultAccountTier !== undefined ? String(activeVaultAccountTier) : null,
        appVersion,
        reportedAt: new Date().toISOString(),
      });
      setSubmitted(true);
    } catch {
      setError('Something went wrong sending your report. Please email support@cedarvaultapp.com directly.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="bug-modal-overlay" onClick={onClose} role="dialog" aria-modal="true" aria-labelledby="bug-modal-title">
      <div className="bug-modal-card" onClick={e => e.stopPropagation()}>
        <div className="bug-modal-header">
          <h2 id="bug-modal-title">Report a Problem</h2>
          <button className="bug-modal-close" onClick={onClose} aria-label="Close">&#x2715;</button>
        </div>

        {submitted ? (
          <div className="bug-modal-success">
            <p>Thank you — your report has been sent. We'll look into it.</p>
            <button className="btn-primary" onClick={onClose}>Close</button>
          </div>
        ) : (
          <>
            <div className="bug-modal-body">
              {!user && (
                <div style={{ marginBottom: '1rem' }}>
                  <label className="bug-modal-label" htmlFor="bug-reporter-email">
                    Your email <span style={{ fontWeight: 400, opacity: 0.7 }}>(optional — so we can follow up)</span>
                  </label>
                  <input
                    id="bug-reporter-email"
                    className="bug-modal-input"
                    type="email"
                    placeholder="you@example.com"
                    value={reporterEmail}
                    onChange={e => setReporterEmail(e.target.value)}
                  />
                </div>
              )}
              <label className="bug-modal-label" htmlFor="bug-description">
                What happened? <span className="bug-modal-required">*</span>
              </label>
              <textarea
                id="bug-description"
                className="bug-modal-textarea"
                placeholder="Describe what you were doing and what went wrong. The more detail, the better."
                value={description}
                onChange={e => setDescription(e.target.value)}
                rows={4}
              />

              <p className="bug-modal-label">
                How bad is it? <span className="bug-modal-required">*</span>
              </p>
              <div className="bug-modal-severity">
                {([
                  { value: 'looks-off',     label: 'Something looks off' },
                  { value: 'cant-complete', label: "I can't complete a task" },
                  { value: 'broken',        label: 'The app is broken' },
                ] as { value: Severity; label: string }[]).map(opt => (
                  <button
                    key={opt.value}
                    className={`bug-severity-btn${severity === opt.value ? ' bug-severity-btn--active' : ''}`}
                    onClick={() => setSeverity(opt.value)}
                    type="button"
                  >
                    {opt.label}
                  </button>
                ))}
              </div>

              <div className="bug-modal-details">
                <button
                  className="bug-details-toggle"
                  onClick={() => setDetailsOpen(o => !o)}
                  type="button"
                  aria-expanded={detailsOpen}
                >
                  Technical details we'll include {detailsOpen ? '▴' : '▾'}
                </button>
                {detailsOpen && (
                  <table className="bug-details-table">
                    <tbody>
                      <tr><td>Page</td><td>{pageUrl}</td></tr>
                      <tr><td>Browser</td><td className="bug-details-ua">{navigator.userAgent}</td></tr>
                      <tr><td>Screen</td><td>{viewport}</td></tr>
                      {activeVaultName && <tr><td>Vault</td><td>{activeVaultName}</td></tr>}
                      {user && <tr><td>Reported by</td><td>{user.email}</td></tr>}
                      <tr><td>App version</td><td>{appVersion}</td></tr>
                    </tbody>
                  </table>
                )}
              </div>

              {error && <AlertMessage type="error" message={error} />}
            </div>

            <div className="bug-modal-footer">
              <button className="btn-text" onClick={onClose} type="button">Cancel</button>
              <button
                className="btn-primary"
                onClick={handleSubmit}
                disabled={!canSubmit}
                type="button"
              >
                {submitting ? 'Sending…' : 'Send Report'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
